import {
    Box,
    type BoxProps,
    Checkbox,
    Group,
    MenuItem,
    type PaperProps,
    Text,
} from '@mantine/core';
import type { DeepKeys, FilterFn } from '@tanstack/react-table';
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_FilterFns,
    MRT_GlobalFilterTextInput,
    type MRT_Localization,
    type MRT_Row,
    type MRT_RowData,
    type MRT_RowSelectionState,
    MRT_ShowHideColumnsButton,
    type MRT_TableOptions,
    MRT_TablePagination,
    MRT_ToggleFiltersButton,
    MRT_ToggleGlobalFilterButton,
    useMantineReactTable,
} from 'mantine-react-table';
import { minimatch } from 'minimatch';
import { type ReactNode, useMemo } from 'react';
import type { Except, Merge, SetRequired } from 'type-fest';
import type { Enum } from '../../types/enum';
import type { If } from '../../types/if';
import type { Not } from '../../types/not';
import type { SetState } from '../../types/setState';
import { tryCatch } from '../../utils/tryCatch';
import { when } from '../../utils/when';
import { Show } from '../control-flow/Show';
import { MantineTableRangeFilter } from './MantineTableRangeFilter';

type AccessorFn<R extends object> = NonNullable<MRT_ColumnDef<R>['accessorFn']>;
type Id<R extends object> = MRT_ColumnDef<R>['id'];

export type MantineColumn<R extends object> = Except<
    SetRequired<MRT_ColumnDef<R>, 'Cell' | 'Header'>,
    | 'accessorKey'
    | 'accessorFn'
    | 'id'
    | 'enableColumnDragging'
    | 'columnFilterModeOptions'
    | 'renderColumnFilterModeMenuItems'
    | 'enableSorting'
    | 'enableGlobalFilter'
    | 'enableColumnFilter'
    | 'enableColumnFilterModes'
    | 'filterFn'
    | 'filterVariant'
    | 'Filter'
    | 'mantineFilterSelectProps'
> &
    (
        | {
              type: Enum<string>;
              formatEnum: (type: Enum<string>, value: string) => string;
          }
        | {
              type: 'number' | 'string' | 'boolean' | 'reference';
              formatEnum?: never;
          }
    ) &
    ({ accessorKey: DeepKeys<R> } | { accessorFn: AccessorFn<R>; id: Id<R> });

type MantineTableOptions<
    R extends object,
    A extends boolean,
    BTA extends boolean,
> = Merge<
    Except<
        MRT_TableOptions<R>,
        | 'columns'
        | 'data'
        | 'layoutMode'
        | 'positionActionsColumn'
        | 'enableDensityToggle'
        | 'enableFullScreenToggle'
        | 'renderTopToolbar'
        | 'renderBottomToolbar'
        | 'enableEditing'
        | 'renderRowActions'
        | 'displayColumnDefOptions'
        | 'mantinePaginationProps'
        | 'renderBottomToolbarCustomActions'
        | 'enableRowSelection'
        | 'onRowSelectionChange'
        | 'filterFns'
        | 'localization'
        | 'enableGlobalFilterModes'
        | 'globalFilterModeOptions'
        | 'globalFilterFn'
        | 'renderGlobalFilterModeMenuItems'
    >,
    {
        initialState?: Omit<
            MRT_TableOptions<R>['initialState'],
            'density' | 'pagination'
        >;
        mantineTableProps?: Omit<
            MRT_TableOptions<R>['mantineTableProps'],
            | 'withRowBorders'
            | 'verticalSpacing'
            | 'horizontalSpacing'
            | 'styles'
        >;
        state?: Omit<MRT_TableOptions<R>['state'], 'rowSelection'>;
    }
> &
    If<
        A,
        {
            renderRowActions: MRT_TableOptions<R>['renderRowActions'];
        }
    > &
    If<
        Not<BTA>,
        {
            renderBottomToolbarCustomActions?: MRT_TableOptions<R>['renderBottomToolbarCustomActions'];
        }
    >;

type MantineTableProps<R extends object, A extends boolean> = {
    columns: Array<MantineColumn<R>>;
    data: Array<R> | null | undefined;
    label: ReactNode;
    initialPageSize?: 5 | 10 | 25 | 50;
    getRowId?: (originalRow: R, index: number, parent?: MRT_Row<R>) => string;
    enableActions: A;
    fillParent?: boolean;
} & If<A, { rowActionsWidth: number; rowActionsHeader: ReactNode }> &
    (
        | {
              tableOptions?: MantineTableOptions<R, A, false>;
              rowSelectionState?: never;
          }
        | {
              tableOptions?: MantineTableOptions<R, A, true>;
              rowSelectionState: {
                  rowSelection: MRT_RowSelectionState;
                  setRowSelection: SetState<MRT_RowSelectionState>;
              };
          }
    );

type CustomFilterKeys = 'equalsStringSensitive' | 'regex' | 'wildcard';
type FilterKeys =
    | keyof Pick<typeof MRT_FilterFns, 'includesStringSensitive'>
    | CustomFilterKeys;
type FilterMapping = Record<FilterKeys, string>;

const filters: FilterMapping = {
    equalsStringSensitive: 'Equals',
    includesStringSensitive: 'Contains',
    regex: 'Regular Expression',
    wildcard: 'Wildcard',
};
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const filterFns = {
    equalsStringSensitive: (row, columnId, filterValue) =>
        row.getValue(columnId) === filterValue,
    regex: (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        if (typeof value !== 'string') {
            return false;
        }
        const regex = tryCatch(() => new RegExp(filterValue)); // TODO display wrong regex?
        return regex.success ? regex.data.test(value) : false;
    },
    wildcard: (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        if (typeof value !== 'string') {
            return false;
        }
        return minimatch(value, filterValue);
    },
} as const satisfies Record<
    CustomFilterKeys,
    FilterFn<unknown>
> as MRT_TableOptions<MRT_RowData>['filterFns']; // TODO remove cast and fix type error
const localization: Partial<MRT_Localization> &
    Record<`filter${Capitalize<CustomFilterKeys>}`, string> = {
    filterEqualsStringSensitive: 'Equals',
    filterRegex: 'Regular Expression',
    filterWildcard: 'Wildcard',
};

export const useMantineTable = <R extends object, A extends boolean>({
    columns,
    data,
    label,
    tableOptions,
    initialPageSize = 5,
    getRowId,
    enableActions,
    rowSelectionState,
    fillParent,
    ...rowActionsProps
}: MantineTableProps<R, A>) => {
    const mappedColumns = useMemo(
        (): Array<MRT_ColumnDef<R>> =>
            columns.map(({ enableHiding = false, type, ...column }) => ({
                ...column,
                ...(type === 'number' && {
                    Filter: MantineTableRangeFilter, // TODO type error when this prop is put in when's number case :\
                }),
                ...(typeof type === 'object' ?
                    {
                        enableColumnFilterModes: false,
                        filterFn: 'equalsStringSensitive',
                        filterVariant: 'select', // TODO 'multi-select' + mantineFilterMultiSelectProps
                        mantineFilterSelectProps: {
                            data: Object.keys(type).map((it) => ({
                                value: it,
                                label:
                                    column.formatEnum ?
                                        column.formatEnum(type, it)
                                    :   it,
                            })),
                        },
                        enableSorting: true,
                        enableGlobalFilter: false,
                        enableColumnFilter: true,
                    }
                :   when({
                        expression: type,
                        cases: {
                            string: {
                                enableColumnFilterModes: true,
                                filterFn: 'includesStringSensitive',
                                enableSorting: true,
                                enableGlobalFilter: true,
                                enableColumnFilter: true,
                            },
                            number: {
                                enableColumnFilterModes: false,
                                filterFn: 'between',
                                filterVariant: 'range',
                                enableSorting: true,
                                enableGlobalFilter: false,
                                enableColumnFilter: true,
                            },
                            boolean: {
                                enableColumnFilterModes: false,
                                filterFn: ((
                                    row: {
                                        getValue: <TValue>(
                                            columnId: string
                                        ) => TValue;
                                    },
                                    columnId,
                                    filterValue
                                ) => {
                                    const value = row.getValue(columnId);
                                    if (typeof value !== 'boolean') {
                                        return false;
                                    }
                                    return value.toString() === filterValue;
                                }) satisfies FilterFn<unknown>,
                                filterVariant: 'select',
                                mantineFilterSelectProps: {
                                    data: [
                                        {
                                            value: 'true',
                                            label: 'True',
                                        },
                                        {
                                            value: 'false',
                                            label: 'False',
                                        },
                                    ],
                                },
                                enableSorting: true,
                                enableGlobalFilter: false,
                                enableColumnFilter: true,
                            },
                            reference: {
                                enableSorting: false,
                                enableGlobalFilter: false,
                                enableColumnFilter: false,
                            },
                        },
                    })),
                enableColumnActions: column.enableColumnActions ?? false,
                enableColumnDragging: false,
                columnFilterModeOptions: Object.keys(filters),
                renderColumnFilterModeMenuItems: ({
                    onSelectFilterMode,
                    column,
                }) =>
                    column.columnDef.columnFilterModeOptions?.map((mode) => (
                        <MenuItem
                            key={mode}
                            onClick={() => onSelectFilterMode(mode)}
                        >
                            {filters[mode as FilterKeys]}
                        </MenuItem>
                    )),
                enableHiding,
                mantineTableHeadCellProps: {
                    style: {
                        justify: 'start',
                        align: 'start',
                    },
                },
            })),
        [columns]
    );

    const customActionFromProps =
        'renderBottomToolbarCustomActions' in rowActionsProps ?
            rowActionsProps.renderBottomToolbarCustomActions
        :   undefined;

    const multiSelectionDisabled =
        tableOptions?.enableMultiRowSelection === false;
    const renderBottomToolbarCustomActions = (
        rowSelectionState && !multiSelectionDisabled ?
            ({ table }) => {
                const numberOfSelected = Object.values(
                    rowSelectionState.rowSelection
                ).reduce((acc, curr) => (curr ? acc + 1 : acc), 0);
                return (
                    <Checkbox
                        label={`${numberOfSelected} selected of ${table.getRowCount()} total`}
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={
                            numberOfSelected !== 0 &&
                            numberOfSelected !== table.getRowCount()
                        }
                        onChange={() => {
                            if (table.getRowCount() < numberOfSelected) {
                                rowSelectionState.setRowSelection({});
                            } else if (table.getIsAllRowsSelected()) {
                                table.toggleAllRowsSelected(false);
                            } else {
                                table.toggleAllRowsSelected(true);
                            }
                        }}
                    />
                );
            }
        :   customActionFromProps) as MRT_TableOptions<R>['renderBottomToolbarCustomActions'];

    const tableState =
        rowSelectionState ?
            {
                ...tableOptions?.state,
                rowSelection: rowSelectionState.rowSelection,
            }
        :   tableOptions?.state;

    return useMantineReactTable({
        ...tableOptions,
        columns: mappedColumns,
        getRowId,
        data: data ?? [],
        layoutMode: 'grid',
        positionActionsColumn: 'last',
        mantinePaginationProps: {
            rowsPerPageOptions: ['5', '10', '25', '50'],
        },
        enableRowSelection: Boolean(rowSelectionState),
        state: tableState,
        onRowSelectionChange: rowSelectionState?.setRowSelection,
        mantinePaperProps: {
            ...tableOptions?.mantinePaperProps,
            style: {
                ...(tableOptions?.mantinePaperProps as PaperProps | undefined)
                    ?.style,
                ...(fillParent ?
                    {
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }
                :   undefined),
            },
        },
        mantineTableContainerProps: {
            ...tableOptions?.mantineTableContainerProps,
            style: {
                ...(
                    tableOptions?.mantineTableContainerProps as
                        | BoxProps
                        | undefined
                )?.style,
                ...(fillParent ? { flexGrow: 1 } : undefined),
            },
        },
        enableHiding: tableOptions?.enableHiding ?? false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableColumnActions: tableOptions?.enableColumnActions ?? false,
        enableColumnFilterModes: true,
        enableGlobalFilterModes: true,
        globalFilterModeOptions: Object.keys(filters),
        globalFilterFn: 'includesStringSensitive',
        renderGlobalFilterModeMenuItems: ({ onSelectFilterMode }) =>
            Object.keys(filters).map((mode) => (
                <MenuItem
                    key={mode}
                    onClick={() => onSelectFilterMode(mode)}
                >
                    {filters[mode as FilterKeys]}
                </MenuItem>
            )),
        filterFns,
        localization,
        enableEditing: enableActions,
        displayColumnDefOptions: {
            'mrt-row-actions': {
                mantineTableHeadCellProps: {
                    align: 'right',
                },
                grow: false,
                size:
                    'rowActionsWidth' in rowActionsProps ?
                        (rowActionsProps.rowActionsWidth as number)
                    :   0,
                Header:
                    'rowActionsHeader' in rowActionsProps ?
                        (rowActionsProps.rowActionsHeader as ReactNode)
                    :   null,
            },
            'mrt-row-select': {
                grow: false,
                size: 40,
            },
            'mrt-row-expand': {
                grow: false,
                size: 40,
            },
            'mrt-row-drag': {
                grow: false,
                size: 40,
                Header: null,
                header: '',
            },
        },
        mantineTableProps: {
            withRowBorders: false,
            verticalSpacing: 4,
            horizontalSpacing: 2,
            styles: { table: { padding: '0 8px' } },
            ...tableOptions?.mantineTableProps,
        },
        initialState: {
            density: 'xs',
            pagination: { pageIndex: 0, pageSize: initialPageSize },
            ...tableOptions?.initialState,
        },
        renderTopToolbar: ({ table }) => (
            <Group
                justify={'space-between'}
                px={8}
                pt={8}
                h={44}
            >
                <Text>{label}</Text>
                <Group gap={0}>
                    <MRT_GlobalFilterTextInput table={table} />
                    <MRT_ToggleGlobalFilterButton table={table} />
                    {tableOptions?.enableHiding && (
                        <MRT_ShowHideColumnsButton table={table} />
                    )}
                    <Show
                        when={
                            mappedColumns.reduce(
                                (acc, column) =>
                                    column.enableSorting ? acc + 1 : acc,
                                0
                            ) > 1
                        }
                    >
                        <MRT_ToggleFiltersButton table={table} />
                    </Show>
                    {tableOptions?.renderTopToolbarCustomActions?.({ table })}
                </Group>
            </Group>
        ),
        renderBottomToolbar: ({ table }) => (
            <Group
                px={'xs'}
                justify={'space-between'}
            >
                <Box py={8}>
                    {renderBottomToolbarCustomActions?.({ table })}
                </Box>
                <MRT_TablePagination table={table} />
            </Group>
        ),
    });
};

export const MantineTable = <R extends object, A extends boolean>(
    props: MantineTableProps<R, A>
) => {
    const table = useMantineTable(props);

    return <MantineReactTable table={table} />;
};
